import React, { useEffect, useState } from 'react';
import { getContent } from '@/lib/api';
import { Seo } from '@/components/Seo';
import { PageHero } from '@/components/PageHero';
import { SectionHeading } from '@/components/SectionHeading';
import { RevealGroup, RevealItem } from '@/components/Reveal';
import { MagneticButton } from '@/components/MagneticButton';
import { CtaSection } from '@/components/CtaSection';
import { EquipImage } from '@/components/EquipImage';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ArrowUpRight, Wrench } from 'lucide-react';

export default function Equipment() {
  const [equipment, setEquipment] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    getContent('equipment').then(setEquipment).catch(() => {});
  }, []);

  return (
    <>
      <Seo title='Equipment & Machinery' description='Hydraulic excavators with rock breakers, crawler drills, backhoe loaders, graders, soil compactors, transit mixers, tankers and more — available for projects and hiring.' path='/equipment' image='/media/img/quarry-equipment.jpg' />
      <PageHero eyebrow='Equipment & Machinery' title='Powered by the right machine.' subtitle='A capable fleet built to move earth, break rock and support heavy projects across Tamil Nadu.' image='/media/img/quarry-equipment.jpg' />

      <section className='bg-[#F6F5F1] py-20 lg:py-28'>
        <div className='container-x'>
          <SectionHeading eyebrow='The Fleet' title='Machinery & capability.' intro='Sixteen core machine types with make and model. Tap any machine for details and hiring.' />
          <RevealGroup className='mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
            {equipment.map((e) => (
              <RevealItem key={e.slug}>
                <button onClick={() => setActive(e)} data-testid='equipment-card' data-cursor='DETAILS' className='group block w-full overflow-hidden rounded-2xl border border-[#20272A]/10 bg-white text-left'>
                  <div className='relative h-60 overflow-hidden'>
                    <EquipImage item={e} />
                    <div className='absolute inset-0' style={{ background: 'linear-gradient(180deg, transparent 45%, rgba(32,39,42,0.75) 100%)' }} />
                    <span className='absolute bottom-4 left-4 right-4'>
                      <span className='block h-px w-0 bg-[#B88A3B] transition-all duration-500 group-hover:w-14' />
                      <span className='mt-2 block text-[11px] uppercase tracking-wider text-[#B88A3B]'>{e.category}</span>
                    </span>
                  </div>
                  <div className='p-6'>
                    <div className='flex items-start justify-between gap-3'>
                      <h3 className='font-head text-xl font-semibold text-[#20272A]'>{e.name}</h3>
                      <ArrowUpRight size={20} className='mt-1 shrink-0 text-[#B88A3B]' />
                    </div>
                    {e.model && <p className='mt-1 font-mono-num text-xs text-[#20272A]/45'>{e.model}</p>}
                    <p className='mt-3 text-sm leading-relaxed text-[#20272A]/60'>{e.description}</p>
                  </div>
                </button>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className='max-w-2xl overflow-hidden border-none p-0'>
          {active && (
            <div>
              <div className='relative h-64'>
                <EquipImage item={active} imgClassName='h-full w-full object-cover' />
                <div className='absolute inset-0' style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(32,39,42,0.85) 100%)' }} />
                <div className='absolute bottom-5 left-6 right-6'>
                  <span className='eyebrow text-[#B88A3B]'>{active.category}</span>
                  <h3 className='mt-1 font-head text-3xl font-semibold text-[#F6F5F1]'>{active.name}</h3>
                  {active.model && <p className='font-mono-num text-sm text-[#F6F5F1]/70'>{active.model}</p>}
                </div>
              </div>
              <div className='p-7'>
                <p className='text-[#20272A]/75'>{active.description}</p>
                <div className='mt-6 grid gap-4 sm:grid-cols-2'>
                  <div className='rounded-xl bg-[#F6F5F1] p-4'>
                    <span className='eyebrow text-[#075B61]'>Related Service</span>
                    <p className='mt-1 text-sm text-[#20272A]/75'>{active.relatedService}</p>
                  </div>
                  <div className='rounded-xl bg-[#F6F5F1] p-4'>
                    <span className='eyebrow text-[#075B61]'>Make / Model</span>
                    <p className='mt-1 text-sm text-[#20272A]/75'>{active.model || 'Available for projects and hiring.'}</p>
                  </div>
                </div>
                {active.hiring && (
                  <div className='mt-6 flex items-center justify-between rounded-xl border border-[#075B61]/15 bg-[#075B61]/5 p-4'>
                    <span className='inline-flex items-center gap-2 text-sm text-[#075B61]'><Wrench size={16} /> Available for hiring</span>
                    <MagneticButton to='/contact' variant='primary' data-cursor='ENQUIRE'>Enquire</MagneticButton>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CtaSection />
    </>
  );
}
